import type { VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithoutRef, ElementRef } from 'react'
import { forwardRef } from 'react'
import { cn } from '@/lib/utils'
import * as LabelPrimitive from '@radix-ui/react-label'
import { cva } from 'class-variance-authority'

// Form labels sit one step quieter than the control they name: 12 px / 500 in
// the muted colour, so the label reads as a caption above the value rather
// than competing with it (design audit S14, label-weight feedback 2026-09-10).
// Block, so a label stacked over its control sits on its own line box: an
// inline label took the parent's taller line height and added ~3 px above
// the control, so hand-written `space-y-1.5` stacks never matched DialogField.
const labelVariants = cva(
  'block text-xs font-medium leading-none text-muted-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
)

const Label = forwardRef<
  ElementRef<typeof LabelPrimitive.Root>,
  ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants> & {
      /** Marks the field required with an asterisk; optional fields carry no marker (design audit S14). */
      required?: boolean
    }
>(({ className, required, children, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  >
    {children}
    {required && (
      <>
        {/* Sized and weighted against the 12 px muted label so the marker
            still reads; the label itself stays quiet (design audit S14). */}
        <span
          aria-hidden="true"
          className="ml-1 align-middle text-sm font-semibold leading-none text-destructive"
        >
          *
        </span>
        <span className="sr-only"> (required)</span>
      </>
    )}
  </LabelPrimitive.Root>
))
Label.displayName = LabelPrimitive.Root.displayName

export { Label }
